/**
 * Workflow Service
 *
 * API service for workflow management.
 */
import api from './api';
import agentService from './agent';
import {
  Workflow,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  WorkflowListResponse,
  WorkflowListParams,
  WorkflowStats,
  RunWorkflowRequest,
  RunWorkflowResponse,
  WorkflowExecutionMode,
  WorkflowTriggerType,
  WorkflowStep,
} from '../types/workflow';

/**
 * Backend workflow response structure (different from frontend Workflow type)
 */
interface BackendWorkflowResponse {
  id: string;
  name: string;
  description: string | null;
  userId: string;
  workspaceId: string | null;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  version: number;
  steps: {
    workflowType?: string;
    steps?: Array<{
      stepId: string;
      type?: string;
      agentId?: string;
      name: string;
      order: number;
      config?: Record<string, unknown>;
      dependsOn?: string[];
      timeoutSeconds?: number;
      maxRetries?: number;
    }>;
    config?: {
      trigger?: {
        type?: string;
        config?: Record<string, unknown>;
      };
      tags?: string[];
    };
  };
  createdAt: string;
  updatedAt: string;
  // Execution stats from backend
  runCount?: number;
  successCount?: number;
  failureCount?: number;
  lastRunAt?: string | null;
}

/**
 * Transform backend workflow response to frontend Workflow type
 */
function transformWorkflow(backend: BackendWorkflowResponse, agents?: Map<string, AgentInfo>): Workflow {
  const stepsData = backend.steps || {};
  const stepsConfig = stepsData.config || {};
  const triggerData = stepsConfig.trigger || {};

  // Transform steps array
  const steps: WorkflowStep[] = (stepsData.steps || []).map((step) => {
    const agent = agents?.get(step.agentId || '');
    const stepType = (step.type as WorkflowStep['type']) || 'agent';
    return {
      id: step.stepId,
      type: stepType,
      name: step.name,
      agentId: step.agentId || '',
      agentName: agent?.name || step.name,
      agentIcon: AGENT_TYPE_ICONS[agent?.type || ''] || '🤖',
      order: step.order,
      config: step.config || {},
      timeoutSeconds: step.timeoutSeconds,
      maxRetries: step.maxRetries,
      dependsOn: step.dependsOn,
    };
  });

  // Map workflow type to execution mode
  const executionModeMap: Record<string, WorkflowExecutionMode> = {
    sequential: 'SEQUENTIAL',
    SEQUENTIAL: 'SEQUENTIAL',
    parallel: 'PARALLEL',
    PARALLEL: 'PARALLEL',
    conditional: 'CONDITIONAL',
    CONDITIONAL: 'CONDITIONAL',
  };

  const executionMode: WorkflowExecutionMode =
    executionModeMap[stepsData.workflowType || 'sequential'] || 'SEQUENTIAL';

  // Map trigger type
  const triggerTypeMap: Record<string, WorkflowTriggerType> = {
    manual: 'MANUAL',
    MANUAL: 'MANUAL',
    scheduled: 'SCHEDULED',
    SCHEDULED: 'SCHEDULED',
    webhook: 'WEBHOOK',
    WEBHOOK: 'WEBHOOK',
    event: 'EVENT',
    EVENT: 'EVENT',
  };

  const triggerType: WorkflowTriggerType =
    triggerTypeMap[triggerData.type || 'MANUAL'] || 'MANUAL';

  return {
    id: backend.id,
    name: backend.name,
    description: backend.description,
    status: backend.status,
    executionMode,
    trigger: {
      type: triggerType,
      config: triggerData.config || {},
    },
    steps,
    version: String(backend.version),
    tags: stepsConfig.tags || [],
    lastRunAt: backend.lastRunAt || null,
    runCount: backend.runCount || 0,
    successCount: backend.successCount || 0,
    failureCount: backend.failureCount || 0,
    createdAt: backend.createdAt,
    updatedAt: backend.updatedAt,
  };
}

interface AgentInfo {
  name: string;
  type: string;
}

/** Fetch all agents and build an id→info lookup map */
async function fetchAgentInfo(): Promise<Map<string, AgentInfo>> {
  try {
    const res = await agentService.list({ limit: 100 });
    const map = new Map<string, AgentInfo>();
    for (const agent of res.data) {
      map.set(agent.id, { name: agent.name, type: agent.type });
    }
    return map;
  } catch {
    return new Map();
  }
}

const AGENT_TYPE_ICONS: Record<string, string> = {
  CUSTOMER_SUPPORT: '💬',
  DATA_PROCESSING: '📊',
  WORKFLOW_AUTOMATION: '⚙️',
  CONTENT_GENERATION: '✍️',
  ANALYSIS: '🔍',
  INTEGRATION: '🔌',
  SALES: '💼',
  MARKETING: '📣',
  HR: '👥',
  FINANCE: '💰',
  CUSTOM: '🤖',
};

export const workflowService = {
  /**
   * Create a new workflow
   *
   * @param data - Workflow creation data
   * @returns Created workflow
   */
  async create(data: CreateWorkflowRequest): Promise<Workflow> {
    const [response, agentInfo] = await Promise.all([
      api.post<BackendWorkflowResponse>('/workflows', data),
      fetchAgentInfo(),
    ]);
    return transformWorkflow(response.data, agentInfo);
  },

  /**
   * Get a single workflow by ID
   *
   * @param workflowId - The workflow ID to fetch
   * @returns Workflow details
   */
  async get(workflowId: string): Promise<Workflow> {
    const [response, agentInfo] = await Promise.all([
      api.get<BackendWorkflowResponse>(`/workflows/${workflowId}`),
      fetchAgentInfo(),
    ]);
    return transformWorkflow(response.data, agentInfo);
  },

  /**
   * List workflows with pagination and filters
   *
   * @param params - Query parameters for filtering and pagination
   * @returns Paginated list of workflows
   */
  async list(params?: WorkflowListParams): Promise<WorkflowListResponse> {
    const [response, agentInfo] = await Promise.all([
      api.get<{ data: BackendWorkflowResponse[]; meta: WorkflowListResponse['meta'] }>('/workflows', {
        params,
      }),
      fetchAgentInfo(),
    ]);
    return {
      data: response.data.data.map((w) => transformWorkflow(w, agentInfo)),
      meta: response.data.meta,
    };
  },

  /**
   * Update a workflow
   *
   * @param workflowId - The workflow ID to update
   * @param data - Update data
   * @returns Updated workflow
   */
  async update(workflowId: string, data: UpdateWorkflowRequest): Promise<Workflow> {
    const [response, agentInfo] = await Promise.all([
      api.put<BackendWorkflowResponse>(`/workflows/${workflowId}`, data),
      fetchAgentInfo(),
    ]);
    return transformWorkflow(response.data, agentInfo);
  },

  /**
   * Delete a workflow
   *
   * @param workflowId - The workflow ID to delete
   */
  async delete(workflowId: string): Promise<void> {
    await api.delete(`/workflows/${workflowId}`);
  },

  /**
   * Activate a workflow
   *
   * @param workflowId - The workflow ID to activate
   * @returns Updated workflow
   */
  async activate(workflowId: string): Promise<Workflow> {
    const response = await api.post<BackendWorkflowResponse>(`/workflows/${workflowId}/activate`);
    return transformWorkflow(response.data);
  },

  /**
   * Pause a workflow
   *
   * @param workflowId - The workflow ID to pause
   * @returns Updated workflow
   */
  async pause(workflowId: string): Promise<Workflow> {
    const response = await api.post<BackendWorkflowResponse>(`/workflows/${workflowId}/pause`);
    return transformWorkflow(response.data);
  },

  /**
   * Archive a workflow
   *
   * @param workflowId - The workflow ID to archive
   * @returns Updated workflow
   */
  async archive(workflowId: string): Promise<Workflow> {
    const response = await api.post<BackendWorkflowResponse>(`/workflows/${workflowId}/archive`);
    return transformWorkflow(response.data);
  },

  /**
   * Run a workflow manually
   *
   * @param workflowId - The workflow ID to run
   * @param data - Optional input data for the workflow
   * @returns Run response with execution ID
   */
  async run(workflowId: string, data?: RunWorkflowRequest): Promise<RunWorkflowResponse> {
    const response = await api.post<{ id: string; status: string; workflowId: string }>(
      `/workflows/${workflowId}/execute`,
      data ? { inputData: data.input } : {}
    );
    // Transform backend response to frontend expected format
    return {
      runId: response.data.id,
      workflowId: response.data.workflowId,
      status: response.data.status === 'PENDING' ? 'QUEUED' : 'STARTED',
      message: `Workflow execution ${response.data.status.toLowerCase()}`,
    };
  },

  /**
   * Clone a workflow
   *
   * @param workflowId - The workflow ID to clone
   * @param name - Name for the cloned workflow
   * @returns Cloned workflow
   */
  async clone(workflowId: string, name: string): Promise<Workflow> {
    const response = await api.post<BackendWorkflowResponse>(`/workflows/${workflowId}/clone`, { name });
    return transformWorkflow(response.data);
  },

  /**
   * Get workflow statistics
   *
   * @returns Workflow statistics
   */
  async getStats(): Promise<WorkflowStats> {
    const response = await api.get<WorkflowStats>('/workflows/stats');
    return response.data;
  },

  /**
   * List all workflow executions across all workflows
   *
   * @param params - Query parameters for filtering and pagination
   * @returns Paginated list of workflow executions
   */
  async listExecutions(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<WorkflowExecutionListResponse> {
    const response = await api.get<WorkflowExecutionListResponse>('/workflows/executions/all', {
      params,
    });
    return response.data;
  },

  /**
   * List executions for a specific workflow
   *
   * @param workflowId - Workflow ID
   * @param params - Query parameters for filtering and pagination
   * @returns Paginated list of workflow executions
   */
  async listWorkflowExecutions(
    workflowId: string,
    params?: { page?: number; limit?: number; status?: string }
  ): Promise<WorkflowExecutionListResponse> {
    const response = await api.get<WorkflowExecutionListResponse>(
      `/workflows/${workflowId}/executions`,
      { params }
    );
    return response.data;
  },

  /**
   * Get workflow execution details
   *
   * @param workflowId - Workflow ID
   * @param executionId - Execution ID
   * @returns Workflow execution details with step executions
   */
  async getExecution(workflowId: string, executionId: string): Promise<WorkflowExecutionDetail> {
    const response = await api.get<WorkflowExecutionDetail>(
      `/workflows/${workflowId}/executions/${executionId}?include_steps=true`
    );
    return response.data;
  },

  /**
   * Replay a workflow execution — re-run with the same input (current definition).
   * Returns the new execution (its `id` is used to navigate to the fresh run).
   */
  async replayExecution(
    workflowId: string,
    executionId: string
  ): Promise<{ id: string; status: string }> {
    const response = await api.post<{ id: string; status: string }>(
      `/workflows/${workflowId}/executions/${executionId}/replay`
    );
    return response.data;
  },
};

/**
 * Workflow execution response from backend
 */
export interface WorkflowExecution {
  id: string;
  workflowId: string;
  workflowName?: string;
  workflowVersion: number;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'PAUSED';
  inputData?: Record<string, unknown>;
  outputData?: Record<string, unknown>;
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  /** Rolled up from the run's agent steps when it reaches a terminal status. */
  totalTokensUsed?: number;
  /** Estimated cost in cents (fractional), rolled up from the run's steps. */
  totalCostCents?: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowExecutionListResponse {
  data: WorkflowExecution[];
  meta: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface WorkflowStepExecution {
  id: string;
  stepId: string;
  stepType: string;
  stepName?: string;
  agentExecutionId?: string;
  status: string;
  inputData?: Record<string, unknown>;
  outputData?: Record<string, unknown>;
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  /** From the step's linked agent execution; absent for control steps. */
  tokensUsed?: number;
  /** Estimated cost in cents (fractional). Absent for control steps. */
  costCents?: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * How many steps a run actually processed, skipped and failed.
 *
 * A status alone cannot answer "did this run do anything?". A workflow whose
 * every branch was skipped finishes COMPLETED and looks identical to one that
 * did all the work — which is how 27 dead branches ran unnoticed for weeks.
 */
export interface StepCounts {
  total: number;
  processed: number;
  skipped: number;
  failed: number;
}

export interface WorkflowExecutionDetail extends WorkflowExecution {
  stepExecutions: WorkflowStepExecution[];
  /** Derived server-side from the step rows, so it cannot disagree with them. */
  stepCounts?: StepCounts;
}

export default workflowService;
